import { useState, useEffect, useRef } from "react";

interface GitHubStats {
  stars: number;
  forks: number;
  downloads: number;
  loading: boolean;
  error: string | null;
}

interface GitHubRelease {
  assets: Array<{
    download_count: number;
  }>;
}

interface GitHubRepo {
  stargazers_count: number;
  forks_count: number;
}

// Cache key for localStorage
const CACHE_KEY = "github-stats-cache";
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

interface CachedStats {
  [key: string]: {
    stats: { stars: number; forks: number; downloads: number };
    timestamp: number;
  };
}

function getCachedStats(owner: string, repo: string): { stars: number; forks: number; downloads: number } | null {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;
    
    const data: CachedStats = JSON.parse(cached);
    const key = `${owner}/${repo}`;
    const entry = data[key];
    
    if (entry && Date.now() - entry.timestamp < CACHE_DURATION) {
      return entry.stats;
    }
  } catch {
    // Ignore cache errors
  }
  return null;
}

function setCachedStats(owner: string, repo: string, stats: { stars: number; forks: number; downloads: number }) {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    const data: CachedStats = cached ? JSON.parse(cached) : {};
    data[`${owner}/${repo}`] = { stats, timestamp: Date.now() };
    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch {
    // Ignore cache errors
  }
}

export function useGitHubStats(owner: string, repo: string): GitHubStats {
  // Try to get cached stats immediately (no loading state if cached)
  const cachedStats = getCachedStats(owner, repo);
  
  const [stats, setStats] = useState<GitHubStats>({
    stars: cachedStats?.stars ?? 0,
    forks: cachedStats?.forks ?? 0,
    downloads: cachedStats?.downloads ?? 0,
    loading: !cachedStats, // Only show loading if no cache
    error: null,
  });
  
  const hasFetched = useRef(false);

  useEffect(() => {
    if (!owner || !repo || hasFetched.current) return;
    
    // Defer API calls to avoid blocking initial render
    const fetchStats = async () => {
      hasFetched.current = true;
      
      try {
        // Fetch both in parallel for speed
        const [repoResponse, releasesResponse] = await Promise.all([
          fetch(`https://api.github.com/repos/${owner}/${repo}`),
          fetch(`https://api.github.com/repos/${owner}/${repo}/releases`)
        ]);
        
        if (!repoResponse.ok) {
          throw new Error("Failed to fetch repo stats");
        }
        
        const repoData: GitHubRepo = await repoResponse.json();
        
        let totalDownloads = 0;
        if (releasesResponse.ok) {
          const releasesData: GitHubRelease[] = await releasesResponse.json();
          // Calculate total downloads from all releases
          for (const release of releasesData) {
            for (const asset of release.assets) {
              totalDownloads += asset.download_count;
            }
          }
        }

        const newStats = {
          stars: repoData.stargazers_count,
          forks: repoData.forks_count,
          downloads: totalDownloads,
        };
        
        // Cache the stats
        setCachedStats(owner, repo, newStats);

        setStats({
          ...newStats,
          loading: false,
          error: null,
        });
      } catch (err) {
        setStats((prev) => ({
          ...prev,
          loading: false,
          error: err instanceof Error ? err.message : "Failed to fetch stats",
        }));
      }
    };

    // Defer fetch to after initial paint - use requestIdleCallback for better performance
    // Falls back to setTimeout for browsers that don't support it
    let cancelled = false;
    
    const scheduleId = 'requestIdleCallback' in globalThis
      ? (globalThis as typeof globalThis & { requestIdleCallback: typeof requestIdleCallback }).requestIdleCallback(() => {
          if (!cancelled) fetchStats();
        }, { timeout: 2000 }) // Max wait 2 seconds
      : setTimeout(() => {
          if (!cancelled) fetchStats();
        }, 500); // Larger delay to not block LCP
    
    return () => {
      cancelled = true;
      if ('cancelIdleCallback' in globalThis && typeof scheduleId === 'number') {
        (globalThis as typeof globalThis & { cancelIdleCallback: typeof cancelIdleCallback }).cancelIdleCallback(scheduleId);
      } else {
        clearTimeout(scheduleId as unknown as ReturnType<typeof setTimeout>);
      }
    };
  }, [owner, repo]);

  return stats;
}
